import { api } from '@/services/api/AxiosBaseService'
import { createCrudService } from '@/services/api/createCrudService'
import type { SingleResponse } from '@/types/api'
import type { AutomationReport } from '@/services/modules/AutomationReportsService'

export type ProposalPipelineStatus = 'EM_NEGOCIACAO' | 'GANHA' | 'PERDIDA'

// ----------------------- Etapas (kanban) -----------------------
export interface ProposalStage {
  id: string
  stage: string
  order: number
  status: 'ATIVO' | 'INATIVO'
}
export const ProposalStagesService = createCrudService<ProposalStage>(
  '/commercial/proposal-stages',
  { searchField: 'stage' },
)

export interface KanbanCard {
  id: string
  companyName: string
  recipientName: string | null
  city: string | null
  livesCount: number
  status: 'RASCUNHO' | 'GERADA'
  pipelineStatus: ProposalPipelineStatus
  tiersCount: number
  canGeneratePdf: boolean
  isGenerated: boolean
  missing: string[]
  createdByName?: string | null
  createdAt?: string | null
  formatted?: { createdAt?: string; proposalDate?: string | null }
}
export interface KanbanColumn {
  id: string
  stage: string
  order: number
  proposals: KanbanCard[]
}

// ----------------------- O que pode ser proposto -----------------------
/**
 * Linha do catálogo como quem MONTA a proposta a vê: modalidades e
 * coberturas, com texto e carência. Sem preço cheio, peso, curva ou versão
 * de tabela — representantes também usam esta tela.
 */
export interface OfferLine {
  id: string
  name: string
  description: string | null
  /** "Modalidades" (Individual, Família…) ou "Níveis" (Smart, Plus, Premium). */
  tierLabel: 'Modalidades' | 'Níveis'
  tiers: { id: string; name: string; beneficiaries: string }[]
  items: OfferItem[]
}

export interface OfferItem {
  id: string
  name: string
  text: string | null
  grace: string | null
  /** Cobertura securitária (seguro). */
  insurance: boolean
  /** Texto por modalidade quando muda (ex.: capital de cada nível do Ouro). */
  tierTexts: { tierId: string; text: string }[]
}

// ----------------------- Simulação -----------------------
/** Valor de uma modalidade. Só o que se mostra ao cliente: por vida e mensal. */
export interface QuoteTier {
  id: string
  name: string
  beneficiaries: string
  total: number
  monthlyTotal: number
}

export interface ProposalQuote {
  livesRequested: number
  livesUsed: number
  livesAdjusted: boolean
  tiers: QuoteTier[]
  monthlyTotal: number
  /** A partir de quantas vidas o valor por vida fica menor (null = já é a última faixa). */
  nextBandFrom: number | null
}

export interface CommercialProposal {
  id: string
  companyName: string
  recipientName: string | null
  recipientEmail: string | null
  recipientPhone: string | null
  city: string | null
  proposalDate: string | null
  additionalText: string | null
  observations: string | null
  status: 'RASCUNHO' | 'GERADA'
  stageId: string | null
  pipelineStatus: ProposalPipelineStatus
  /** Ultimo PDF concluido desta proposta - ausente quando nunca foi gerada. */
  lastPdf?: { url: string | null; generatedAt: string | null; formatted?: { generatedAt: string | null } } | null
  stage?: { id: string; stage: string; order: number } | null

  // Simulador
  livesCount: number
  proposalCategoryId: string | null
  category?: { id: string; name: string; slug: string } | null
  pricingTable?: { id: string; version: number; name: string; status: string } | null
  tierIds?: string[]
  tiers?: { id: string; name: string }[]
  itemIds?: string[]
  items?: { id: string; name: string }[]

  // Dados das partes (PJ) — endereço destrinchado para o contrato
  document: string | null
  address: string | null
  number: string | null
  complement: string | null
  district: string | null
  postalCode: string | null
  state: string | null
  legalRepName: string | null
  legalRepDocument: string | null
  /** Cidade que abre a assinatura do PDF — não é a cidade do cliente. */
  signingCityOptionId: string | null
  signingCity?: string | null

  // Vigência e pagamento — sempre seleção
  validityStart: string | null
  contractTypeOptionId: string | null
  minMonthsOptionId: string | null
  paymentDayOptionId: string | null
  firstPaymentOptionId: string | null
  terms?: {
    contractType?: string | null
    minMonths?: string | null
    paymentDay?: string | null
    firstPayment?: string | null
  }
  paymentMethodIds?: string[]
  paymentMethods?: string[]

  textBlockIds?: string[]
  textBlocks?: { id: string; kind: string; title: string | null }[]

  /**
   * Aprovação do texto adicional — o único trecho livre do documento.
   * `blocked` significa que o PDF não sai enquanto alguém não decidir.
   */
  approval?: {
    status: 'NAO_REQUERIDA' | 'PENDENTE' | 'APROVADA' | 'REPROVADA'
    pending: boolean
    blocked: boolean
    note: string | null
    at: string | null
    by?: string | null
  }

  creator?: { id: string; name: string } | null
  createdAt?: string
  formatted?: { createdAt?: string }
}

export interface CommercialProposalPayload {
  companyName?: string
  recipientName?: string | null
  recipientEmail?: string | null
  recipientPhone?: string | null
  city?: string | null
  proposalDate?: string | null
  additionalText?: string | null
  observations?: string | null
  status?: 'RASCUNHO' | 'GERADA'
  stageId?: string | null

  // Simulador
  proposalCategoryId?: string | null
  livesCount?: number
  tierIds?: string[]
  itemIds?: string[]

  // Dados das partes (PJ)
  document?: string | null
  address?: string | null
  number?: string | null
  complement?: string | null
  district?: string | null
  postalCode?: string | null
  state?: string | null
  legalRepName?: string | null
  legalRepDocument?: string | null
  signingCityOptionId?: string | null

  // Vigência e pagamento (IDs de opção cadastrada)
  validityStart?: string | null
  contractTypeOptionId?: string | null
  minMonthsOptionId?: string | null
  paymentDayOptionId?: string | null
  firstPaymentOptionId?: string | null
  paymentMethodIds?: string[]

  textBlockIds?: string[]
}

const base = createCrudService<CommercialProposal, CommercialProposalPayload>(
  '/commercial/proposals',
  { searchField: 'company_name' },
)

export const ProposalsService = {
  ...base,

  /** Linhas que podem ser propostas (com a versão de preço da proposta, se já existe). */
  async catalog(proposalId?: string): Promise<OfferLine[]> {
    const r = await api.get<{ data: OfferLine[] }>('/commercial/proposals/catalog', {
      params: proposalId ? { proposal_id: proposalId } : undefined,
    })
    return r.data.data
  },

  /** Simulação ao vivo (não salva) — usada enquanto a proposta é montada. */
  async simulate(payload: {
    proposalId?: string
    proposalCategoryId: string
    livesCount: number
    tierIds: string[]
    itemIds: string[]
  }): Promise<ProposalQuote> {
    const r = await api.post<SingleResponse<ProposalQuote>>('/commercial/proposals/simulate', payload)
    return r.data.data
  },

  /** Dispara a geração do PDF (assíncrona). Retorna o AutomationReport p/ polling. */
  async generate(id: string): Promise<AutomationReport> {
    const r = await api.post<SingleResponse<AutomationReport>>(`/commercial/proposals/${id}/generate`)
    return r.data.data
  },

  /** Dispara a geração do Manual de Utilização (assíncrona). */
  async generateManual(id: string): Promise<AutomationReport> {
    const r = await api.post<SingleResponse<AutomationReport>>(`/commercial/proposals/${id}/generate-manual`)
    return r.data.data
  },

  /** Kanban: colunas (etapas ATIVAS) + propostas EM_NEGOCIACAO. */
  async kanban(userId?: string): Promise<{ columns: KanbanColumn[]; canFilterByUser: boolean }> {
    const r = await api.get<{ data: KanbanColumn[]; meta?: { canFilterByUser?: boolean } }>(
      '/commercial/proposal-stages/kanban',
      { params: { user_id: userId || undefined } },
    )
    return { columns: r.data.data, canFilterByUser: Boolean(r.data.meta?.canFilterByUser) }
  },

  /** Usuários com proposta no board (filtro do kanban). */
  async kanbanUsers(): Promise<{ id: string; name: string }[]> {
    const r = await api.get<{ data: { id: string; name: string }[] }>('/commercial/proposal-stages/kanban-users')
    return r.data.data
  },

  async updateStage(id: string, stageId: string): Promise<CommercialProposal> {
    const r = await api.post<SingleResponse<CommercialProposal>>(`/commercial/proposals/${id}/update-stage`, { stage_id: stageId })
    return r.data.data
  },
  async win(id: string): Promise<CommercialProposal> {
    return (await api.post<SingleResponse<CommercialProposal>>(`/commercial/proposals/${id}/win`)).data.data
  },
  async lose(id: string): Promise<CommercialProposal> {
    return (await api.post<SingleResponse<CommercialProposal>>(`/commercial/proposals/${id}/lose`)).data.data
  },
  async reopen(id: string): Promise<CommercialProposal> {
    return (await api.post<SingleResponse<CommercialProposal>>(`/commercial/proposals/${id}/reopen`)).data.data
  },

  /** Libera o texto adicional — sem isso o PDF não é gerado. */
  async approve(id: string, note?: string): Promise<CommercialProposal> {
    return (await api.post<SingleResponse<CommercialProposal>>(`/commercial/proposals/${id}/approve`, { note: note || null })).data.data
  },
  /** Reprova o texto adicional. O motivo é obrigatório. */
  async reject(id: string, note: string): Promise<CommercialProposal> {
    return (await api.post<SingleResponse<CommercialProposal>>(`/commercial/proposals/${id}/reject`, { note })).data.data
  },
}
